import Vue from 'vue';
import AjaxUtils from "../../util/ajax_utils";
import { ApckWizard, ApckWizardPane } from '../wizard/wizard';
import { delay } from '../_ui/_utils/useUiUtils';

// Individual scope table component for each target type
export const ScopeTargetTable = {
    props: {
        src: {
            type: String,
            required: true
        },
        targetType: {
            type: String,
            required: true // 'application', 'api', 'infrastructure'
        },
        title: {
            type: String,
            required: true
        },
        search: {
            type: String,
            default: ''
        }
    },
    data: function () {
        return {
            cols: [
                {"data": "target", "heading": "Scope Item", "canSearch": true, "width": "45%"},
                {"data": "internal", "heading": "Internal", "ctype": "boolcolumn", "width": 100, "center": true},
                {"data": "size", "heading": "Scope Size", "width": 120},
                {"data": "anniversary", "heading": "Can Edit From", "ctype": "datecolumnnew", "width": 140},
            ],
            actions: [{
                title: "Remove From Scope",
                icon: "fa-trash",
                class: "btn-negative",
                handler: this.deleteScope,
                useOwnCol: true,
                hideLabel: true,
            }],
        };
    },
    computed: {
        srcUrl() {
            var url = this.src + '/list?type=' + this.targetType;
            if (this.search) {
                url += '&search=' + encodeURIComponent(this.search);
            }
            return url;
        }
    },
    methods: {
        deleteScope(row) {
            const cmp = this;
            const id = row.get("_id");
            const target = row.get("target");
            const url = `${this.src}/remove/${id}`;

            cmp.$dialog.confirm(
                `Are you sure you want to remove "${target}" from scope?`,
                "Remove from scope",
                function(){
                    AjaxUtils.ajax_get({
                        url: url,
                        success: () => {
                            cmp.reload();
                            cmp.$emit('scope-updated');
                        },
                        fail: () => {
                            cmp.reload();
                        },
                    });
                }
            );
        },
        reload() {
            this.$refs.table.store.reload();
        }
    },
    template: `
        <apck-table
            ref="table"
            :columns="cols"
            :actions="actions"
            :src="srcUrl"
            :limit="50"
            :border="false"
            :show-toolbar="false"
            newStyleTable
        />
    `
}

// Main scope management component
export const ApckScopeManagementForm = {
    components: {
        'apck-scope-target-table': ScopeTargetTable,
        'apck-wizard': ApckWizard,
        'apck-wizard-pane': ApckWizardPane,
    },
    props: {
        baseUrl: {
            default: '/scope-management',
            type: String
        },
        listURL: {
            default: '/scope-management/list',
            type: String
        },
        addURL: {
            default: '/scope-management/add',
            type: String
        }
    },
    data: function() {
        return {
            // Search
            search: '',
            appliedSearch: '',

            // Wizard state
            addScopeModal: {
                render: false,
                open: false
            },
            wizardStep: 0,
            
            // Scope targets for each type
            webAppTargets: "",
            apiTargets: "",
            infraTargets: "",
            
            // Validated targets
            validatedWebAppTargets: [],
            validatedApiTargets: [],
            validatedInfraTargets: [],
            
            // Validation state
            validating: false,
            validationComplete: false,
            
            // Loading states
            loading: false,
            submitting: false,
            
            // Wizard error for inline display
            wizardError: null,
            
            // Protocol confirmation dialog
            protocolConfirmDialog: {
                show: false,
                message: '',
                protocols: '',
                count: 0,
                callback: null
            },
            
            // Scope limits
            unlimited: false,
            max_infra: 0,
            used_infra: 0,
            disable_infra: false,
            max_apps: 0,
            used_apps: 0,
            disable_apps: false,
            max_workspaces: 0,
            used_workspaces: 0,
            used_api_urls: 0,
            max_api_urls: 0,
            internal_available: false,
            
            // Permissions
            hasApiExplorer: false,

            // Export state
            exporting: false,
        };
    },
    computed: {
        remainingApps() {
            if (this.max_apps === 0) return Infinity;
            return Math.max(0, this.max_apps - this.used_apps);
        },
        remainingApis() {
            if (this.max_api_urls === 0) return Infinity;
            return Math.max(0, this.max_api_urls - this.used_api_urls);
        },
        remainingInfra() {
            if (this.max_infra === 0) return Infinity;
            return Math.max(0, this.max_infra - this.used_infra);
        },
        remainingWorkspaces() {
            if (this.max_workspaces === 0) return Infinity;
            return Math.max(0, this.max_workspaces - this.used_workspaces);
        },
        proposedApps() {
            return this.validatedWebAppTargets.filter(t => t.valid && !t.is_blacklisted).length;
        },
        proposedApis() {
            return this.validatedApiTargets.filter(t => t.valid && !t.is_blacklisted).length;
        },
        proposedInfra() {
            return this.validatedInfraTargets
                .filter(t => t.valid && !t.is_blacklisted && !t.is_application)
                .reduce((sum, t) => sum + (t.scope_size || t.size || 1), 0);
        },
        wouldExceedAppsLimit() {
            if (this.max_apps === 0) return false;
            return (this.used_apps + this.proposedApps) > this.max_apps;
        },
        wouldExceedApisLimit() {
            if (this.max_api_urls === 0) return false;
            return (this.used_api_urls + this.proposedApis) > this.max_api_urls;
        },
        wouldExceedInfraLimit() {
            if (this.max_infra === 0) return false;
            return (this.used_infra + this.proposedInfra) > this.max_infra;
        },
        canSubmitScope() {
            return this.webAppTargets.trim() || this.apiTargets.trim() || this.infraTargets.trim();
        },
        hasInternalTargets() {
            return this.validatedInfraTargets.some(t => t.is_internal) ||
                   this.validatedWebAppTargets.some(t => t.is_internal) ||
                   this.validatedApiTargets.some(t => t.is_internal);
        },
        hasValidationErrors() {
            const allTargets = [
                ...this.validatedWebAppTargets,
                ...this.validatedApiTargets,
                ...this.validatedInfraTargets
            ];
            return allTargets.some(t => !t.valid || t.is_blacklisted);
        },
        allValidatedTargets() {
            return [
                ...this.validatedWebAppTargets.map(t => ({ ...t, type: 'Web Application' })),
                ...this.validatedApiTargets.map(t => ({ ...t, type: 'API' })),
                ...this.validatedInfraTargets.map(t => ({ ...t, type: 'Infrastructure' }))
            ];
        },
        targetsWithOverlaps() {
            // Find all targets that have the has_overlap flag set (server-side overlaps)
            return this.allValidatedTargets.filter(t => t.has_overlap && t.valid);
        },
        clientSideOverlaps() {
            // Detect overlaps within the current validation session
            // i.e., same FQDN being added as multiple types (Web App + API, etc.)
            const targetMap = new Map();
            const overlaps = [];
            
            // Helper to normalise target for comparison
            const normaliseTarget = (target) => {
                let normalised = target.trim().toLowerCase();
                normalised = normalised.replace('https://', '').replace('http://', '');
                if (normalised.endsWith('/')) {
                    normalised = normalised.slice(0, -1);
                }
                return normalised;
            };
            
            // Group targets by normalised FQDN
            this.allValidatedTargets.forEach(target => {
                if (!target.valid || target.is_blacklisted) return;
                
                const normalised = normaliseTarget(target.target);
                if (!targetMap.has(normalised)) {
                    targetMap.set(normalised, []);
                }
                targetMap.get(normalised).push(target);
            });
            
            // Find FQDNs that appear with multiple types
            targetMap.forEach((targets, fqdn) => {
                if (targets.length > 1) {
                    const types = [...new Set(targets.map(t => t.type))];
                    if (types.length > 1) {
                        // This FQDN has multiple scan types
                        overlaps.push({
                            fqdn: targets[0].target,
                            types: types,
                            targets: targets
                        });
                    }
                }
            });
            
            return overlaps;
        }
    },
    methods: {
        async openAddScopeModal() {
            this.addScopeModal.render = true;
            await delay(0);
            this.addScopeModal.open = true;
        },
        normaliseTargetForComparison(targetStr) {
            // Helper to normalise target for comparison
            let normalised = targetStr.trim().toLowerCase();
            normalised = normalised.replace('https://', '').replace('http://', '');
            if (normalised.endsWith('/')) {
                normalised = normalised.slice(0, -1);
            }
            return normalised;
        },
        targetsOverlap(target1, target2) {
            // Check if two targets overlap (one contains the other)
            const norm1 = this.normaliseTargetForComparison(target1);
            const norm2 = this.normaliseTargetForComparison(target2);
            return norm1 === norm2 || norm1.includes(norm2) || norm2.includes(norm1);
        },
        isClientSideOverlap(target) {
            const normalisedTarget = this.normaliseTargetForComparison(target.target);
            return this.clientSideOverlaps.some(overlap => {
                const normalisedOverlap = this.normaliseTargetForComparison(overlap.fqdn);
                return normalisedTarget === normalisedOverlap;
            });
        },
        removeTargetFromReview(target) {
            // Remove target from the appropriate validated array based on type
            if (target.type === 'Web Application') {
                const index = this.validatedWebAppTargets.findIndex(t => t.target === target.target);
                if (index !== -1) {
                    this.validatedWebAppTargets.splice(index, 1);
                }
            } else if (target.type === 'API') {
                const index = this.validatedApiTargets.findIndex(t => t.target === target.target);
                if (index !== -1) {
                    this.validatedApiTargets.splice(index, 1);
                }
            } else if (target.type === 'Infrastructure') {
                const index = this.validatedInfraTargets.findIndex(t => t.target === target.target);
                if (index !== -1) {
                    this.validatedInfraTargets.splice(index, 1);
                }
            }
        },
        async closeAddScopeModal() {
            this.addScopeModal.open = false;
            await delay(400);
            this.addScopeModal.render = false;
            // Reset wizard and inputs
            this.wizardStep = 0;
            this.webAppTargets = "";
            this.apiTargets = "";
            this.infraTargets = "";
            this.validatedWebAppTargets = [];
            this.validatedApiTargets = [];
            this.validatedInfraTargets = [];
            this.validationComplete = false;
            this.wizardError = null;
        },
        updateScopeStats() {
            const cmp = this;
            const url = cmp.listURL;

            cmp.loading = true;
            AjaxUtils.ajax_get({
                url: url,
                success: function (data) {
                    if (data && data.success) {
                        cmp.unlimited = data.unlimited || false;
                        
                        if (data.details) {
                            cmp.max_infra = data.details.max_infra || 0;
                            cmp.used_infra = data.details.used_infra || 0;
                            cmp.disable_infra = data.details.disable_ips || false;
                            cmp.max_apps = data.details.max_apps || 0;
                            cmp.used_apps = data.details.used_apps || 0;
                            cmp.disable_apps = data.details.disable_urls || false;
                            cmp.used_workspaces = data.details.used_workspaces || 0;
                            cmp.max_workspaces = data.details.max_workspaces || 0;
                            cmp.used_api_urls = data.details.used_api_urls || 0;
                            cmp.max_api_urls = data.details.max_api_urls || 0;
                        }
                        
                        cmp.internal_available = data.internal_available || false;
                    }
                    cmp.loading = false;
                },
                fail: function () {
                    cmp.loading = false;
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    flash_404_errors: false
                }
            });
        },
        reloadTables() {
            if (this.$refs.webAppTable) this.$refs.webAppTable.reload();
            if (this.$refs.apiTable) this.$refs.apiTable.reload();
            if (this.$refs.infraTable) this.$refs.infraTable.reload();
        },
        onSearchInput() {
            var cmp = this;
            if (cmp._searchTimer) clearTimeout(cmp._searchTimer);
            cmp._searchTimer = setTimeout(function () {
                cmp.appliedSearch = cmp.search;
            }, 400);
        },
        handleScopeUpdated() {
            this.updateScopeStats();
            this.reloadTables();
        },
        goToAssetDiscovery() {
            window.location.href = '/asset-discovery';
        },
        exportScope() {
            const cmp = this;
            if (cmp.exporting) return;
            cmp.exporting = true;

            AjaxUtils.ajax_get({
                url: cmp.baseUrl + '/export',
                success: function (json) {
                    if (json.status === 'completed' && json.download_link) {
                        window.location = json.download_link;
                        cmp.exporting = false;
                    } else if (json.poll_url) {
                        cmp._pollExport(json.poll_url);
                    } else {
                        cmp.$flash({ type: 'error', message: 'Failed to start export' });
                        cmp.exporting = false;
                    }
                },
                fail: function () {
                    cmp.$flash({ type: 'error', message: 'Failed to start export' });
                    cmp.exporting = false;
                }
            });
        },
        _pollExport(pollUrl) {
            const cmp = this;
            setTimeout(function poll() {
                AjaxUtils.ajax_get({
                    url: pollUrl,
                    success: function (json) {
                        if (json.status === 'completed' && json.download_link) {
                            window.location = json.download_link;
                            cmp.exporting = false;
                        } else if (json.status === 'ongoing' || json.status === 'starting') {
                            setTimeout(poll, 2000);
                        } else {
                            cmp.$flash({ type: 'error', message: json.message || 'Export failed' });
                            cmp.exporting = false;
                        }
                    },
                    fail: function () {
                        cmp.$flash({ type: 'error', message: 'Failed to check export status' });
                        cmp.exporting = false;
                    }
                });
            }, 1000);
        },
        async confirmProtocolConversion(unsupportedList, targetType) {
            const cmp = this;
            const protocols = unsupportedList.map(u => u.protocol).join(', ');
            const count = unsupportedList.length;
            
            return new Promise((resolve) => {
                const message = `Found ${count} target(s) with unsupported protocol(s): ${protocols}. Would you like to convert them to use https:// instead?`;
                
                cmp.protocolConfirmDialog.show = true;
                cmp.protocolConfirmDialog.message = message;
                cmp.protocolConfirmDialog.protocols = protocols;
                cmp.protocolConfirmDialog.count = count;
                cmp.protocolConfirmDialog.callback = resolve;
            });
        },
        acceptProtocolConversion() {
            if (this.protocolConfirmDialog.callback) {
                this.protocolConfirmDialog.callback(true);
            }
            this.protocolConfirmDialog.show = false;
            this.protocolConfirmDialog.callback = null;
        },
        denyProtocolConversion() {
            if (this.protocolConfirmDialog.callback) {
                this.protocolConfirmDialog.callback(false);
            }
            this.protocolConfirmDialog.show = false;
            this.protocolConfirmDialog.callback = null;
        },
        handleTextareaKeydown(event, validateFunction) {
            // Check if Enter was pressed
            if (event.key === 'Enter') {
                event.preventDefault();
                validateFunction();
            }
        },
        detectUnsupportedProtocols(targets, targetType) {
            // Detect targets with unsupported protocols for applications and APIs
            if (targetType === 'application' || targetType === 'api') {
                const unsupported = [];
                const lines = targets.split('\n');
                
                lines.forEach(line => {
                    const trimmed = line.trim();
                    if (!trimmed) return;
                    
                    // Check if it has an invalid protocol (contains :// but not http/https)
                    const protocolMatch = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/(.+)$/);
                    if (protocolMatch && protocolMatch[1] !== 'http' && protocolMatch[1] !== 'https') {
                        unsupported.push({
                            original: trimmed,
                            protocol: protocolMatch[1],
                            rest: protocolMatch[2]
                        });
                    }
                });
                
                return unsupported;
            }
            return [];
        },
        preprocessTargets(targets, targetType, forceConvert = false) {
            // Preprocess targets to validate and fix protocols for applications and APIs
            if (targetType === 'application' || targetType === 'api') {
                return targets.split('\n').map(line => {
                    const trimmed = line.trim();
                    if (!trimmed) return trimmed;
                    
                    // Check if it starts with a valid protocol
                    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
                        return trimmed;
                    }
                    
                    // Check if it has an invalid protocol (contains :// but not http/https)
                    const protocolMatch = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/(.+)$/);
                    if (protocolMatch) {
                        // Invalid protocol - replace with https:// if forceConvert is true
                        if (forceConvert) {
                            return 'https://' + protocolMatch[2];
                        }
                        // Otherwise keep as-is for now (will be filtered out)
                        return trimmed;
                    }
                    
                    // No protocol at all - add https://
                    return 'https://' + trimmed;
                }).join('\n');
            }
            return targets;
        },
        async validateTargets(targets, targetType, forceConvert = false) {
            const cmp = this;
            
            if (!targets.trim()) {
                cmp.$flash({ type: 'warning', message: 'Please enter at least one target to validate' });
                return [];
            }
            
            const url = `${cmp.baseUrl}/validate`;
            
            // Preprocess targets with forceConvert flag
            const processedTargets = cmp.preprocessTargets(targets, targetType, forceConvert);
            
            const _data = {
                targets: targetType === 'api' ? '' : processedTargets,
                api_targets: targetType === 'api' ? processedTargets : '',
                target_type: targetType
            };
            
            return new Promise((resolve, reject) => {
                AjaxUtils.ajax_post({
                    url: url,
                    data: JSON.stringify(_data),
                    success: function (data) {
                        if (data.success) {
                            resolve(data.targets || []);
                        } else {
                            reject(data.message || 'Failed to validate targets');
                        }
                    },
                    fail: function (data) {
                        reject(data && data.message ? data.message : 'Failed to validate targets');
                    },
                    show_error_flash: false,
                });
            });
        },
        async validateWebAppTargets() {
            const cmp = this;
            
            console.log(cmp.webAppTargets);

            if (!cmp.webAppTargets.trim()) {
                return;
            }
            
            // Check for unsupported protocols
            const unsupported = cmp.detectUnsupportedProtocols(cmp.webAppTargets, 'application');
            let forceConvert = false;
            if (unsupported.length > 0) {
                const shouldConvert = await cmp.confirmProtocolConversion(unsupported, 'application');
                if (!shouldConvert) {
                    // User chose not to convert - clear the input
                    cmp.webAppTargets = '';
                    cmp.wizardError = {
                        type: 'info',
                        message: 'Input cleared. Please enter targets with http:// or https:// protocols only.'
                    };
                    return;
                }
                // User confirmed - set flag to convert during preprocessing
                forceConvert = true;
            }
            
            // Check for client-side duplicates within the same scope type
            const newTargets = cmp.webAppTargets.split('\n').map(t => t.trim()).filter(t => t);
            const duplicatesInScope = [];
            
            newTargets.forEach(newTarget => {
                for (const existingTarget of cmp.validatedWebAppTargets) {
                    if (cmp.targetsOverlap(newTarget, existingTarget.target)) {
                        duplicatesInScope.push(newTarget);
                        break;
                    }
                }
            });
            
            if (duplicatesInScope.length > 0) {
                cmp.wizardError = {
                    type: 'error',
                    message: `Cannot add duplicate targets within Web Applications: ${duplicatesInScope.join(', ')} - These targets overlap with existing targets in your validation list.`
                };
                return;
            }
            
            try {
                cmp.validating = true;
                const results = await cmp.validateTargets(cmp.webAppTargets, 'application', forceConvert);
                
                // Separate valid and invalid targets
                const validTargets = results.filter(t => t.valid && !t.is_blacklisted);
                const invalidTargets = results.filter(t => !t.valid || t.is_blacklisted);
                
                // Check for duplicates and block if found
                const duplicates = results.filter(t => t.is_duplicate);
                if (duplicates.length > 0) {
                    cmp.wizardError = {
                        type: 'error',
                        message: `Cannot add duplicate targets: ${duplicates.map(t => t.target).join(', ')} - These targets are already in your scope.`
                    };
                    cmp.validating = false;
                    return;
                }
                
                // Check if adding these would exceed limits
                const proposedTotal = cmp.validatedWebAppTargets.filter(t => t.valid && !t.is_blacklisted).length + validTargets.length;
                if (cmp.max_apps > 0 && (cmp.used_apps + proposedTotal) > cmp.max_apps) {
                    cmp.wizardError = {
                        type: 'error', 
                        message: `Cannot add ${validTargets.length} target(s). This would exceed your licence limit of ${cmp.max_apps} applications. You have ${cmp.remainingApps} slots remaining.` 
                    };
                    cmp.validating = false;
                    return;
                }
                
                // Append new results to existing validated list
                cmp.validatedWebAppTargets = [...cmp.validatedWebAppTargets, ...results];
                
                // Remove valid targets from textarea, keep invalid ones for correction
                if (invalidTargets.length > 0) {
                    cmp.webAppTargets = invalidTargets.map(t => t.target).join('\n');
                    cmp.wizardError = {
                        type: 'warning', 
                        message: `${validTargets.length} target(s) validated successfully. ${invalidTargets.length} target(s) need correction.` 
                    };
                } else {
                    cmp.webAppTargets = '';
                    cmp.wizardError = {
                        type: 'info',
                        message: `${validTargets.length} web application target(s) validated successfully`
                    };
                }
                
                cmp.validating = false;
            } catch (error) {
                cmp.validating = false;
                cmp.wizardError = {
                    type: 'error',
                    message: error
                };
            }
        },
        async validateApiTargets() {
            const cmp = this;
            
            if (!cmp.apiTargets.trim()) {
                return;
            }
            
            // Check for unsupported protocols
            const unsupported = cmp.detectUnsupportedProtocols(cmp.apiTargets, 'api');
            let forceConvert = false;
            if (unsupported.length > 0) {
                const shouldConvert = await cmp.confirmProtocolConversion(unsupported, 'api');
                if (!shouldConvert) {
                    // User chose not to convert - clear the input
                    cmp.apiTargets = '';
                    cmp.wizardError = {
                        type: 'info',
                        message: 'Input cleared. Please enter targets with http:// or https:// protocols only.'
                    };
                    return;
                }
                // User confirmed - set flag to convert during preprocessing
                forceConvert = true;
            }
            
            // Check for client-side duplicates within the same scope type
            const newTargets = cmp.apiTargets.split('\n').map(t => t.trim()).filter(t => t);
            const duplicatesInScope = [];
            
            newTargets.forEach(newTarget => {
                for (const existingTarget of cmp.validatedApiTargets) {
                    if (cmp.targetsOverlap(newTarget, existingTarget.target)) {
                        duplicatesInScope.push(newTarget);
                        break;
                    }
                }
            });
            
            if (duplicatesInScope.length > 0) {
                cmp.wizardError = {
                    type: 'error',
                    message: `Cannot add duplicate targets within API Targets: ${duplicatesInScope.join(', ')} - These targets overlap with existing targets in your validation list.`
                };
                return;
            }
            
            try {
                cmp.validating = true;
                const results = await cmp.validateTargets(cmp.apiTargets, 'api', forceConvert);
                
                // Separate valid and invalid targets
                const validTargets = results.filter(t => t.valid && !t.is_blacklisted);
                const invalidTargets = results.filter(t => !t.valid || t.is_blacklisted);
                
                // Check for duplicates and block if found
                const duplicates = results.filter(t => t.is_duplicate);
                if (duplicates.length > 0) {
                    cmp.wizardError = {
                        type: 'error',
                        message: `Cannot add duplicate targets: ${duplicates.map(t => t.target).join(', ')} - These targets are already in your scope.`
                    };
                    cmp.validating = false;
                    return;
                }
                
                // Check if adding these would exceed limits
                const proposedTotal = cmp.validatedApiTargets.filter(t => t.valid && !t.is_blacklisted).length + validTargets.length;
                if (cmp.max_api_urls > 0 && (cmp.used_api_urls + proposedTotal) > cmp.max_api_urls) {
                    cmp.wizardError = {
                        type: 'error', 
                        message: `Cannot add ${validTargets.length} target(s). This would exceed your licence limit of ${cmp.max_api_urls} API targets. You have ${cmp.remainingApis} slots remaining.` 
                    };
                    cmp.validating = false;
                    return;
                }
                
                // Append new results to existing validated list
                cmp.validatedApiTargets = [...cmp.validatedApiTargets, ...results];
                
                // Remove valid targets from textarea, keep invalid ones for correction
                if (invalidTargets.length > 0) {
                    cmp.apiTargets = invalidTargets.map(t => t.target).join('\n');
                    cmp.wizardError = {
                        type: 'warning', 
                        message: `${validTargets.length} target(s) validated successfully. ${invalidTargets.length} target(s) need correction.` 
                    };
                } else {
                    cmp.apiTargets = '';
                    cmp.wizardError = {
                        type: 'info',
                        message: `${validTargets.length} API target(s) validated successfully`
                    };
                }
                
                cmp.validating = false;
            } catch (error) {
                cmp.validating = false;
                cmp.wizardError = {
                    type: 'error',
                    message: error
                };
            }
        },
        async validateInfraTargets() {
            const cmp = this;
            
            if (!cmp.infraTargets.trim()) {
                return;
            }
            
            // Check for client-side duplicates within the same scope type
            const newTargets = cmp.infraTargets.split('\n').map(t => t.trim()).filter(t => t);
            const duplicatesInScope = [];
            
            newTargets.forEach(newTarget => {
                for (const existingTarget of cmp.validatedInfraTargets) {
                    if (cmp.targetsOverlap(newTarget, existingTarget.target)) {
                        duplicatesInScope.push(newTarget);
                        break;
                    }
                }
            });
            
            if (duplicatesInScope.length > 0) {
                cmp.wizardError = {
                    type: 'error',
                    message: `Cannot add duplicate targets within Infrastructure: ${duplicatesInScope.join(', ')} - These targets overlap with existing targets in your validation list.`
                };
                return;
            }
            
            try {
                cmp.validating = true;
                const results = await cmp.validateTargets(cmp.infraTargets, 'infrastructure');
                
                // Separate valid and invalid targets
                const validTargets = results.filter(t => t.valid && !t.is_blacklisted);
                const invalidTargets = results.filter(t => !t.valid || t.is_blacklisted);
                
                // Check for duplicates and block if found
                const duplicates = results.filter(t => t.is_duplicate);
                if (duplicates.length > 0) {
                    cmp.wizardError = {
                        type: 'error',
                        message: `Cannot add duplicate targets: ${duplicates.map(t => t.target).join(', ')} - These targets are already in your scope.`
                    };
                    cmp.validating = false;
                    return;
                }
                
                // Check for internal targets without internal hub (check ALL results, not just valid ones)
                const internalTargets = results.filter(t => t.is_internal);
                if (internalTargets.length > 0 && !cmp.internal_available) {
                    cmp.wizardError = {
                        type: 'error',
                        message: `Cannot add internal infrastructure targets: Your organisation does not have an internal scanning hub configured. Please contact support or remove internal targets (${internalTargets.map(t => t.target).join(', ')}).`
                    };
                    cmp.validating = false;
                    return;
                }
                
                // Calculate scope size for new valid targets
                const newScopeSize = validTargets.reduce((sum, t) => sum + (t.scope_size || t.size || 1), 0);
                const existingScopeSize = cmp.validatedInfraTargets
                    .filter(t => t.valid && !t.is_blacklisted && !t.is_application)
                    .reduce((sum, t) => sum + (t.scope_size || t.size || 1), 0);
                
                // Check if adding these would exceed limits
                if (cmp.max_infra > 0 && (cmp.used_infra + existingScopeSize + newScopeSize) > cmp.max_infra) {
                    cmp.wizardError = {
                        type: 'error', 
                        message: `Cannot add ${validTargets.length} target(s) (${newScopeSize} IPs). This would exceed your licence limit of ${cmp.max_infra} IPs. You have ${cmp.remainingInfra} slots remaining.` 
                    };
                    cmp.validating = false;
                    return;
                }
                
                // Append new results to existing validated list
                cmp.validatedInfraTargets = [...cmp.validatedInfraTargets, ...results];
                
                // Remove valid targets from textarea, keep invalid ones for correction
                if (invalidTargets.length > 0) {
                    cmp.infraTargets = invalidTargets.map(t => t.target).join('\n');
                    cmp.wizardError = {
                        type: 'warning', 
                        message: `${validTargets.length} target(s) validated successfully. ${invalidTargets.length} target(s) need correction.` 
                    };
                } else {
                    cmp.infraTargets = '';
                    cmp.wizardError = {
                        type: 'info',
                        message: `${validTargets.length} infrastructure target(s) validated successfully`
                    };
                }
                
                cmp.validating = false;
            } catch (error) {
                cmp.validating = false;
                cmp.wizardError = {
                    type: 'error',
                    message: error
                };
            }
        },
        handleWizardStep(stepIndex, callback) {
            const cmp = this;
            
            // Check if we have any validated targets
            const hasValidatedTargets = cmp.validatedWebAppTargets.length > 0 || 
                                       cmp.validatedApiTargets.length > 0 || 
                                       cmp.validatedInfraTargets.length > 0;
            
            if (!hasValidatedTargets) {
                // No validated targets yet, allow progression
                callback(null, true);
                return;
            }
            
            // Client-side validation - check if limits would be exceeded
            if (cmp.wouldExceedAppsLimit) {
                cmp.wizardError = {
                    type: 'error',
                    message: `Adding ${cmp.proposedApps} web application target(s) would exceed your licence limit of ${cmp.max_apps}`
                };
                callback('Licence limit exceeded', false);
                return;
            }
            
            if (cmp.wouldExceedApisLimit) {
                cmp.wizardError = {
                    type: 'error',
                    message: `Adding ${cmp.proposedApis} API target(s) would exceed your licence limit of ${cmp.max_api_urls}`
                };
                callback('Licence limit exceeded', false);
                return;
            }
            
            if (cmp.wouldExceedInfraLimit) {
                cmp.wizardError = {
                    type: 'error',
                    message: `Adding infrastructure targets would exceed your licence limit of ${cmp.max_infra} IPs`
                };
                callback('Licence limit exceeded', false);
                return;
            }
            
            // Server-side validation on the final step before review
            // This provides a final check in case the client-side data is stale
            if (stepIndex >= 2 && hasValidatedTargets) {
                const url = `${cmp.baseUrl}/check-limits`;
                const _data = {
                    validated_web_apps: cmp.validatedWebAppTargets,
                    validated_api_targets: cmp.validatedApiTargets,
                    validated_infra_targets: cmp.validatedInfraTargets
                };
                
                // Important: Don't call callback immediately, wait for ajax to complete
                AjaxUtils.ajax_post({
                    url: url,
                    data: JSON.stringify(_data),
                    success: function (data) {
                        if (data.success && data.can_add) {
                            cmp.wizardError = null;
                            callback(null, true);
                        } else {
                            cmp.wizardError = {
                                type: 'error',
                                message: data.message || 'The selected targets would exceed your licence limits'
                            };
                            callback(data.message || 'Licence limits exceeded', false);
                        }
                    },
                    fail: function (jqXHR, textStatus, errorThrown) {
                        console.error('Error checking scope limits:', textStatus, errorThrown, jqXHR);
                        const errorMsg = (jqXHR.responseJSON && jqXHR.responseJSON.message) 
                            ? jqXHR.responseJSON.message 
                            : 'Unable to verify licence limits, please ensure targets are within your allocation';
                        cmp.wizardError = {
                            type: 'warning',
                            message: errorMsg
                        };
                        // Fail open - allow progression with warning
                        callback(null, true);
                    },
                    show_error_flash: false,
                });
                // Don't call callback here - it's called in the ajax handlers above
                return;
            } else {
                // Early steps, client-side validation passed
                cmp.wizardError = null;
                callback(null, true);
            }
        },
        submitScope() {
            const cmp = this;
            
            // Check if we have any validated targets
            const hasValidatedTargets = cmp.validatedWebAppTargets.length > 0 || 
                                       cmp.validatedApiTargets.length > 0 || 
                                       cmp.validatedInfraTargets.length > 0;
            
            if (!hasValidatedTargets) {
                cmp.wizardError = {
                    type: 'error',
                    message: 'Please validate at least one target before submitting'
                };
                return;
            }
            
            const url = cmp.addURL;
            
            // Collect only valid, non-blacklisted targets from validated arrays
            // If no internal hub is available, also filter out internal targets
            const validWebAppTargets = cmp.validatedWebAppTargets
                .filter(t => t.valid && !t.is_blacklisted && (cmp.internal_available || !t.is_internal))
                .map(t => t.target);
            
            const validInfraTargets = cmp.validatedInfraTargets
                .filter(t => t.valid && !t.is_blacklisted && (cmp.internal_available || !t.is_internal))
                .map(t => t.target);
            
            const validApiTargets = cmp.validatedApiTargets
                .filter(t => t.valid && !t.is_blacklisted && (cmp.internal_available || !t.is_internal))
                .map(t => t.target);
            
            // Combine web app and infrastructure targets for the 'targets' field
            const allTargets = [...validWebAppTargets, ...validInfraTargets];
            
            const _data = {
                "targets": allTargets.join('\n'),
                "api_targets": validApiTargets.join('\n')
            };
            
            cmp.submitting = true;
            AjaxUtils.ajax_post({
                url: url,
                data: JSON.stringify(_data),
                success: function (data) {
                    if (data.success) {
                        // Success - flash on main page after closing modal
                        cmp.$flash({
                            type: 'success',
                            message: data.message || 'Targets added to scope successfully'
                        });
                        cmp.submitting = false;
                        cmp.closeAddScopeModal();
                        cmp.handleScopeUpdated();
                    } else {
                        // Error - show in wizard since modal stays open
                        cmp.wizardError = {
                            type: 'error',
                            message: data.message || 'Failed to add targets to scope'
                        };
                        cmp.submitting = false;
                    }
                },
                fail: function (data) {
                    const message = data && data.message 
                        ? data.message 
                        : 'Failed to update scope. Please try again.';
                    // Error - show in wizard since modal stays open
                    cmp.wizardError = {
                        type: 'error',
                        message: message
                    };
                    cmp.submitting = false;
                },
                show_error_flash: false,
            });
        }
    },
    mounted: function() {
        this.hasApiExplorer = this.$permissions?.api_explorer || false;
        this.updateScopeStats();
    },
    template: `
        <ui-page title="Target Scope" stickyTitle>
            <template #title-bar-buttons>
                <ui-button
                    label="Asset Discovery"
                    iconLeft="fa-binoculars"
                    type="outline"
                    @click="goToAssetDiscovery"
                />
                <ui-button
                    v-if="!unlimited"
                    label="Export to XLSX"
                    iconLeft="fa-file-excel"
                    type="outline"
                    :loading="exporting"
                    @click="exportScope"
                />
                <ui-button
                    v-if="!unlimited"
                    label="Add Scope"
                    iconLeft="fa-plus"
                    type="primary"
                    @click="openAddScopeModal"
                />
            </template>

            <template #content>
                <ui-layout direction="column">
                    <!-- Search -->
                    <ui-layout v-if="!unlimited" align="center">
                        <ui-input-text
                            placeholder="Search by host, URL, IP or CIDR (e.g. 10.0.0.0/24)"
                            :modelValue="search"
                            @update:modelValue="search = $event; onSearchInput()"
                            iconLeft="fa-search"
                            style="min-width: 300px; max-width: 500px;"
                        />
                    </ui-layout>

                    <!-- Unlimited scope message -->
                    <ui-panel-base v-if="unlimited" class="panel">
                        <ui-layout direction="column" gap="4" align="center" justify="center" class="unlimited-scope-content">
                            <ui-icon icon="fa-infinity" type="primary" size="3x" />
                            <ui-heading tag="h3">Unlimited Scope</ui-heading>
                            <span>
                                Your organisation has an unlimited scope. You can scan any target without restrictions.
                            </span>
                        </ui-layout>
                    </ui-panel-base>

                    <!-- Web Applications Section -->
                    <ui-panel-base v-if="!unlimited && !disable_apps" class="panel">
                        <ui-layout direction="column" gap="2">
                            <ui-layout gap="2" align="center" justify="space-between" full-width>
                                <ui-layout gap="2" align="center">
                                    <ui-icon icon="fa-globe" type="primary" />
                                    <ui-heading tag="h3">Web Applications</ui-heading>
                                </ui-layout>
                                <ui-layout gap="2" align="center">
                                    <span v-if="max_apps > 0">
                                        <strong>{{ used_apps }}</strong> of <strong>{{ max_apps }}</strong> used
                                    </span>
                                    <span v-else>
                                        <strong>{{ used_apps }}</strong> in use
                                    </span>
                                    <ui-badge 
                                        v-if="remainingApps !== Infinity"
                                        :label="remainingApps + ' remaining'" 
                                        :color="remainingApps > 0 ? 'green' : 'red'"
                                        fill
                                    />
                                </ui-layout>
                            </ui-layout>
                            <apck-scope-target-table
                                ref="webAppTable"
                                :src="baseUrl"
                                :search="appliedSearch"
                                target-type="application"
                                title="Web Applications"
                                @scope-updated="handleScopeUpdated"
                            />
                        </ui-layout>
                    </ui-panel-base>

                    <!-- API Targets Section -->
                    <ui-panel-base v-if="!unlimited && !disable_apps && hasApiExplorer" class="panel">
                        <ui-layout direction="column" gap="2">
                            <ui-layout gap="2" align="center" justify="space-between" full-width>
                                <ui-layout gap="2" align="center">
                                    <ui-icon icon="fa-brackets-curly" type="primary" />
                                    <ui-heading tag="h3">API Targets</ui-heading>
                                </ui-layout>
                                <ui-layout gap="2" align="center">
                                    <span v-if="max_api_urls > 0">
                                        <strong>{{ used_api_urls }}</strong> of <strong>{{ max_api_urls }}</strong> used
                                    </span>
                                    <span v-else>
                                        <strong>{{ used_api_urls }}</strong> in use
                                    </span>
                                    <ui-badge 
                                        v-if="remainingApis !== Infinity"
                                        :label="remainingApis + ' remaining'" 
                                        :color="remainingApis > 0 ? 'green' : 'red'"
                                        fill
                                    />
                                </ui-layout>
                            </ui-layout>
                            <apck-scope-target-table
                                ref="apiTable"
                                :src="baseUrl"
                                :search="appliedSearch"
                                target-type="api"
                                title="API Targets"
                                @scope-updated="handleScopeUpdated"
                            />
                        </ui-layout>
                    </ui-panel-base>

                    <!-- Infrastructure Section -->
                    <ui-panel-base v-if="!unlimited && !disable_infra" class="panel">
                        <ui-layout direction="column" gap="2">
                            <ui-layout gap="2" align="center" justify="space-between" full-width>
                                <ui-layout gap="2" align="center">
                                    <ui-icon icon="fa-server" type="primary" />
                                    <ui-heading tag="h3">Infrastructure</ui-heading>
                                </ui-layout>
                                <ui-layout gap="2" align="center">
                                    <span v-if="max_infra > 0">
                                        <strong>{{ used_infra }}</strong> of <strong>{{ max_infra }}</strong> used
                                    </span>
                                    <span v-else>
                                        <strong>{{ used_infra }}</strong> in use
                                    </span>
                                    <ui-badge 
                                        v-if="remainingInfra !== Infinity"
                                        :label="remainingInfra + ' remaining'" 
                                        :color="remainingInfra > 0 ? 'green' : 'red'"
                                        fill
                                    />
                                </ui-layout>
                            </ui-layout>
                            <apck-scope-target-table
                                ref="infraTable"
                                :src="baseUrl"
                                :search="appliedSearch"
                                target-type="infrastructure"
                                title="Infrastructure"
                                @scope-updated="handleScopeUpdated"
                            />
                        </ui-layout>
                    </ui-panel-base>

                    <!-- Internal Hub Availability Notice -->
                    <ui-panel-base v-if="!unlimited" class="panel">
                        <ui-layout gap="2" align="center">
                            <ui-icon 
                                :icon="internal_available ? 'fa-check-circle' : 'fa-info-circle'" 
                                :type="internal_available ? 'success' : 'info'" 
                            />
                            <span v-if="internal_available">
                                You have internal hubs available and can add internal scope targets.
                            </span>
                            <span v-else>
                                You do not have an internal hub available. Internal scope targets cannot be scanned.
                            </span>
                        </ui-layout>
                    </ui-panel-base>
                </ui-layout>

                <!-- Add Scope Modal with Wizard -->
                <ui-modal
                    v-if="addScopeModal.render"
                    title="Add Targets to Scope"
                    size="xl"
                    align="center"
                    fullHeight
                    :open.sync="addScopeModal.open"
                    @close="closeAddScopeModal()"
                    class="scope-wizard-modal"
                >
                    <template #content>
                        <!-- Protocol Confirmation Dialog (inline to stay above modal) -->
                        <div v-if="protocolConfirmDialog.show" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 10000; display: flex; align-items: center; justify-content: center;">
                            <div style="background: white; padding: 20px; border-radius: 4px; box-shadow: 0 2px 10px rgba(0,0,0,0.3); max-width: 500px; width: 90%;">
                                <h3 style="margin-top: 0;">Unsupported Protocol Detected</h3>
                                <p style="margin: 15px 0;">{{ protocolConfirmDialog.message }}</p>
                                <div style="display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px;">
                                    <button class="btn btn-positive btn-short" @click="acceptProtocolConversion">Yes, Convert</button>
                                    <button class="btn btn-negative btn-short" @click="denyProtocolConversion">No, Cancel</button>
                                </div>
                            </div>
                        </div>

                        <!-- Inline flash message area -->
                        <div v-if="wizardError" :class="['wizard-inline-flash', 'wizard-inline-flash--' + wizardError.type]">
                            <ui-layout gap="2" align="center">
                                <ui-icon 
                                    :icon="wizardError.type === 'error' ? 'fa-circle-exclamation' : 'fa-triangle-exclamation'" 
                                    :type="wizardError.type === 'error' ? 'destructive' : 'warning'" 
                                />
                                <span>{{ wizardError.message }}</span>
                            </ui-layout>
                        </div>
                        
                        <apck-wizard
                            ref="addScopeWizard"
                            class="scope-wizard"
                            @validate-step="handleWizardStep"
                            @close="closeAddScopeModal"
                            @complete="submitScope"
                            :disabled="submitting"
                        >
                            <!-- Web Applications Step -->
                            <apck-wizard-pane title="Web Applications" v-if="!disable_apps">
                                <ui-layout direction="column" gap="4">
                                    <ui-layout direction="column" gap="2">
                                        <ui-layout gap="2" align="center">
                                            <ui-icon icon="fa-globe" type="primary" />
                                            <ui-heading tag="h4">Web Application Targets</ui-heading>
                                        </ui-layout>
                                        <p>
                                            AppCheck performs Dynamic Application Security Testing (DAST) of live web applications by making thousands of bespoke HTTP requests and analysing the responses, actively probing for weaknesses.
                                        </p>
                                        <p>
                                            To do this, you will configure scans with the root URLs of your applications. These are your web application targets.
                                        </p>
                                        <p>
                                            Each unique Fully Qualified Domain Name (FQDN) counts as one web application target, regardless of protocol (HTTP/HTTPS) or path. Subdomains or non-standard ports count as additional targets.
                                        </p>
                                        <ui-heading tag="h5">Format</ui-heading>
                                        <p>
                                            Each target should be specified as URL or FQDN. Specifying protocol and path are optional, since these are not used to differentiate web application targets. If not specified, https:// will be added automatically to form a valid URL.
                                        </p>
                                        <ui-panel-accordion title="Examples" titleSize="sm" :defaultOpen="false">
                                            <ui-layout direction="column" gap="2">
                                                <pre class="wizard-code-block">
payment.example.com
http://example.com
http://example.com:8080
                                                </pre>
                                                <p class="wizard-secondary-text">
                                                    The above defines three separate web application targets, since they are on different subdomains, or use non-standard ports.
                                                </p>
                                                <pre class="wizard-code-block">
example.com
http://example.com
https://example.com
http://example.com/test
                                                </pre>
                                                <p class="wizard-secondary-text">
                                                    The above defines a single web application target, since all entries share one FQDN and use standard HTTP(S) ports.
                                                </p>
                                            </ui-layout>
                                        </ui-panel-accordion>
                                        <ui-heading tag="h5">Before you can scan a target, it must be added to your organisation's target scope. You can add additional targets below.</ui-heading>
                                    </ui-layout>

                                    <ui-layout gap="2" align="center" v-if="max_apps > 0">
                                        <ui-icon icon="fa-info-circle" type="info" />
                                        <span class="wizard-secondary-text">
                                            You have <strong>{{ remainingApps }}</strong> web application slots remaining.
                                        </span>
                                    </ui-layout>

                                    <!-- Validation Results Table -->
                                    <ui-layout v-if="validatedWebAppTargets.length > 0" direction="column" gap="2">
                                        <ui-heading tag="h5">Validated Targets</ui-heading>
                                        <table class="validation-table">
                                            <thead>
                                                <tr>
                                                    <th>Target</th>
                                                    <th>Status</th>
                                                    <th>Type</th>
                                                    <th>Details</th>
                                                    <th class="action-column">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(target, index) in validatedWebAppTargets" :key="target.target" :class="{'validation-error': !target.valid || target.is_blacklisted}">
                                                    <td class="target-cell">{{ target.target }}</td>
                                                    <td>
                                                        <ui-icon v-if="target.valid && !target.is_blacklisted" icon="fa-check-circle" type="success" />
                                                        <ui-icon v-else icon="fa-times-circle" type="destructive" />
                                                    </td>
                                                    <td>
                                                        <ui-badge v-if="target.is_application" label="Web App" color="blue" />
                                                        <ui-badge v-else-if="target.is_infrastructure" label="Infrastructure" color="red" />
                                                    </td>
                                                    <td>
                                                        <span v-if="target.error_message" class="error-message">{{ target.error_message }}</span>
                                                        <span v-else-if="target.has_overlap" class="warning-message">
                                                            <ui-icon icon="fa-triangle-exclamation" type="warning" />
                                                            {{ target.overlap_message }}
                                                        </span>
                                                        <span v-else-if="target.valid">Valid target</span>
                                                    </td>
                                                    <td>
                                                        <ui-button
                                                            label="Remove"
                                                            iconLeft="fa-trash"
                                                            color="red"
                                                            size="sm"
                                                            @click="validatedWebAppTargets.splice(index, 1)"
                                                            :disabled="submitting || validating"
                                                        />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </ui-layout>

                                    <ui-input-textarea
                                        label="Web Application URLs"
                                        :modelValue="webAppTargets"
                                        @update:modelValue="webAppTargets = $event"
                                        @keydown.native="handleTextareaKeydown($event, validateWebAppTargets)"
                                        placeholder="Enter one URL per line:
example.com
app.example.com
staging.example.com

Press Enter to add targets"
                                        :rows="6"
                                        :disabled="submitting || validating"
                                    />

                                    <ui-button
                                        label="Add Targets"
                                        iconLeft="fa-check"
                                        type="primary"
                                        color="green"
                                        @click="validateWebAppTargets"
                                        :disabled="submitting || validating"
                                    />
                                </ui-layout>
                            </apck-wizard-pane>

                            <!-- API Targets Step (only if API Explorer enabled) -->
                            <apck-wizard-pane title="API Targets" v-if="hasApiExplorer && !disable_apps">
                                <ui-layout direction="column" gap="4">
                                    <ui-layout direction="column" gap="2">
                                        <ui-layout gap="2" align="center">
                                            <ui-icon icon="fa-brackets-curly" type="primary" />
                                            <ui-heading tag="h4">API Targets</ui-heading>
                                        </ui-layout>
                                        <p>
                                            AppCheck performs a specialised form of Dynamic Application Scanning Testing (DAST) targeting APIs. This involves sending thousands of bespoke HTTP requests to your API endpoints and analysing the results, actively probing for weaknesses.
                                        </p>
                                        <p>
                                            Each unique Fully Qualified Domain Name (FQDN) counts as one API target, regardless of protocol (HTTP/HTTPS), path, or number of endpoints. Subdomains or non-standard ports count as additional targets.
                                        </p>
                                        <ui-heading tag="h5">Format</ui-heading>
                                        <p>
                                            Each target should be specified as URL or FQDN. Specifying protocol and path are optional, since these are not used to differentiate web application targets. If not specified, https:// will be added automatically to form a valid URL.
                                        </p>
                                        <ui-panel-accordion title="Examples" titleSize="sm" :defaultOpen="false">
                                            <ui-layout direction="column" gap="2">
                                                <pre class="wizard-code-block">api.example.com https://example.com/api http://api.example.com:8080</pre>
                                                <p class="wizard-secondary-text">
                                                    The above defines three separate API targets, since they are on different subdomains, or use non-standard ports.
                                                </p>
                                                <pre class="wizard-code-block">api.example.com https://api.example.com https://api.example.com/v1</pre>
                                                <p class="wizard-secondary-text">
                                                    The above defines a single API target, since all entries share one FQDN and use standard HTTP(S) ports.
                                                </p>
                                            </ui-layout>
                                        </ui-panel-accordion>
                                        <ui-heading tag="h5">Before you can scan a target, it must be added to your organisation's target scope. You can add additional targets below.</ui-heading>
                                    </ui-layout>

                                    <ui-layout gap="2" align="center" v-if="max_api_urls > 0">
                                        <ui-icon icon="fa-info-circle" type="info" />
                                        <span class="wizard-secondary-text">
                                            You have <strong>{{ remainingApis }}</strong> API target slots remaining.
                                        </span>
                                    </ui-layout>

                                    <!-- Validation Results Table -->
                                    <ui-layout v-if="validatedApiTargets.length > 0" direction="column" gap="2">
                                        <ui-heading tag="h5">Validated Targets</ui-heading>
                                        <table class="validation-table">
                                            <thead>
                                                <tr>
                                                    <th>Target</th>
                                                    <th>Status</th>
                                                    <th>Type</th>
                                                    <th>Details</th>
                                                    <th class="action-column">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(target, index) in validatedApiTargets" :key="target.target" :class="{'validation-error': !target.valid || target.is_blacklisted}">
                                                    <td class="target-cell">{{ target.target }}</td>
                                                    <td>
                                                        <ui-icon v-if="target.valid && !target.is_blacklisted" icon="fa-check-circle" type="success" />
                                                        <ui-icon v-else icon="fa-times-circle" type="destructive" />
                                                    </td>
                                                    <td>
                                                        <ui-badge label="API" color="green" />
                                                    </td>
                                                    <td>
                                                        <span v-if="target.error_message" class="error-message">{{ target.error_message }}</span>
                                                        <span v-else-if="target.has_overlap" class="warning-message">
                                                            <ui-icon icon="fa-triangle-exclamation" type="warning" />
                                                            {{ target.overlap_message }}
                                                        </span>
                                                        <span v-else-if="target.valid">Valid API target</span>
                                                    </td>
                                                    <td>
                                                        <ui-button
                                                            label="Remove"
                                                            iconLeft="fa-trash"
                                                            color="red"
                                                            size="sm"
                                                            @click="validatedApiTargets.splice(index, 1)"
                                                            :disabled="submitting || validating"
                                                        />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </ui-layout>

                                    <ui-input-textarea
                                        label="API URLs"
                                        :modelValue="apiTargets"
                                        @update:modelValue="apiTargets = $event"
                                        @keydown.native="handleTextareaKeydown($event, validateApiTargets)"
                                        placeholder="Enter one API URL per line:
api.example.com
api.example.com/v2

Press Enter to add targets"
                                        :rows="5"
                                        :disabled="submitting || validating"
                                    />

                                    <ui-button
                                        label="Add Targets"
                                        iconLeft="fa-check"
                                        type="primary"
                                        color="green"
                                        @click="validateApiTargets"
                                        :disabled="submitting || validating"
                                    />
                                </ui-layout>
                            </apck-wizard-pane>

                            <!-- Infrastructure Step -->
                            <apck-wizard-pane title="Infrastructure" v-if="!disable_infra">
                                <ui-layout direction="column" gap="4">
                                    <!-- Warning banner for missing internal hub -->
                                    <ui-panel-base v-if="!internal_available" class="panel warning-panel">
                                        <ui-layout gap="2" align="center">
                                            <ui-icon icon="fa-exclamation-triangle" type="warning" />
                                            <span>
                                                <strong>Note:</strong> Your organisation does not have an internal scanning hub configured. 
                                                You cannot add internal infrastructure targets (private IP addresses: 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16) 
                                                until an internal hub is deployed. Please contact support if you need to scan internal infrastructure.
                                            </span>
                                        </ui-layout>
                                    </ui-panel-base>
                                    
                                    <ui-layout direction="column" gap="2">
                                        <ui-layout gap="2" align="center">
                                            <ui-icon icon="fa-server" type="primary" />
                                            <ui-heading tag="h4">Infrastructure Targets</ui-heading>
                                        </ui-layout>
                                        <p>
                                            AppCheck performs vulnerability scans of your infrastructure, scanning for open ports and probing them for vulnerabilities. Scans can be performed unauthenticated, to identify external attack surfaces, or with authentication credentials to obtain a complete inventory of services and vulnerabilities on a machine.
                                        </p>
                                        <p>
                                            Each address consumes a slot in your license. To calculate your total slot requirement, add up all of the addresses specified in your scope. We accept individual IP addresses, hostnames, CIDR notation ranges and IP ranges. Addresses in CIDR ranges and IP ranges count individually towards scope size. Hostnames (where we resolve the IP at scan time) count as a single address per hostname.
                                        </p>
                                        <ui-heading tag="h5">Format</ui-heading>
                                        <p>
                                            Infrastructure targets can be in any of the following formats:
                                        </p>
                                        <ul class="wizard-list">
                                            <li><strong>Individual IP address:</strong> 203.0.113.42</li>
                                            <li><strong>Hostname:</strong> example.com</li>
                                            <li><strong>CIDR range:</strong> 203.0.113.0/24</li>
                                            <li><strong>IP range:</strong> 203.0.113.1-254</li>
                                        </ul>
                                        <ui-panel-accordion title="Examples" titleSize="sm" :defaultOpen="false">
                                            <ui-layout direction="column" gap="2">
                                                <pre class="wizard-code-block">203.0.113.42</pre>
                                                <p class="wizard-secondary-text">
                                                    The above defines one infrastructure target.
                                                </p>
                                                <pre class="wizard-code-block">203.0.113.0/24</pre>
                                                <p class="wizard-secondary-text">
                                                    The above defines 256 infrastructure targets (203.0.113.0 through 203.0.113.255).
                                                </p>
                                                <pre class="wizard-code-block">203.0.113.1-254</pre>
                                                <p class="wizard-secondary-text">
                                                    The above defines 254 infrastructure targets (203.0.113.1 through 203.0.113.254).
                                                </p>
                                                <pre class="wizard-code-block">example.com</pre>
                                                <p class="wizard-secondary-text">
                                                    The above defines one infrastructure target, since the IP address will be resolved at scan time.
                                                </p>
                                            </ui-layout>
                                        </ui-panel-accordion>
                                        <ui-heading tag="h5">Before you can scan a target, it must be added to your organisation's target scope. You can add additional targets below.</ui-heading>
                                    </ui-layout>

                                    <ui-layout gap="2" align="center" v-if="max_infra > 0">
                                        <ui-icon icon="fa-info-circle" type="info" />
                                        <span class="wizard-secondary-text">
                                            You have <strong>{{ remainingInfra }}</strong> infrastructure slots remaining.
                                        </span>
                                    </ui-layout>

                                    <!-- Validation Results Table -->
                                    <ui-layout v-if="validatedInfraTargets.length > 0" direction="column" gap="2">
                                        <ui-heading tag="h5">Validated Targets</ui-heading>
                                        <table class="validation-table">
                                            <thead>
                                                <tr>
                                                    <th>Target</th>
                                                    <th>Status</th>
                                                    <th>Network Type</th>
                                                    <th>Scope Size</th>
                                                    <th>Details</th>
                                                    <th class="action-column">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(target, index) in validatedInfraTargets" :key="target.target" :class="{'validation-error': !target.valid || target.is_blacklisted, 'validation-warning': target.requires_internal_hub}">
                                                    <td class="target-cell">{{ target.target }}</td>
                                                    <td>
                                                        <ui-icon v-if="target.valid && !target.is_blacklisted" icon="fa-check-circle" type="success" />
                                                        <ui-icon v-else-if="target.requires_internal_hub" icon="fa-exclamation-triangle" type="warning" />
                                                        <ui-icon v-else icon="fa-times-circle" type="destructive" />
                                                    </td>
                                                    <td>
                                                        <ui-badge v-if="target.is_internal" label="Internal" color="orange" />
                                                        <ui-badge v-else-if="target.is_external" label="External" color="blue" />
                                                        <ui-badge v-else label="Unknown" color="light-slate" />
                                                    </td>
                                                    <td>{{ target.scope_size || target.size || 1 }}</td>
                                                    <td>
                                                        <span v-if="target.error_message" class="error-message">{{ target.error_message }}</span>
                                                        <span v-else-if="target.has_overlap" class="warning-message">
                                                            <ui-icon icon="fa-triangle-exclamation" type="warning" />
                                                            {{ target.overlap_message }}
                                                        </span>
                                                        <span v-else-if="target.requires_internal_hub" class="warning-message">Internal hub required</span>
                                                        <span v-else-if="target.valid">Valid target</span>
                                                    </td>
                                                    <td>
                                                        <ui-button
                                                            label="Remove"
                                                            iconLeft="fa-trash"
                                                            color="red"
                                                            size="sm"
                                                            @click="validatedInfraTargets.splice(index, 1)"
                                                            :disabled="submitting || validating"
                                                        />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </ui-layout>

                                    <ui-input-textarea
                                        label="Infrastructure Targets"
                                        :modelValue="infraTargets"
                                        @update:modelValue="infraTargets = $event"
                                        @keydown.native="handleTextareaKeydown($event, validateInfraTargets)"
                                        placeholder="Enter one target per line:
example.com
203.0.113.1
203.0.113.0/24

Press Enter to add targets"
                                        :rows="6"
                                        :disabled="submitting || validating"
                                    />

                                    <ui-button
                                        label="Add Targets"
                                        iconLeft="fa-check"
                                        type="primary"
                                        color="green"
                                        @click="validateInfraTargets"
                                        :disabled="submitting || validating"
                                    />
                                </ui-layout>
                            </apck-wizard-pane>

                            <!-- Internal Targets Warning Step (only if internal targets detected) -->
                            <apck-wizard-pane title="Internal Targets" v-if="hasInternalTargets && !internal_available">
                                <ui-layout direction="column" gap="4">
                                    <ui-layout direction="column" gap="2">
                                        <ui-layout gap="2" align="center">
                                            <ui-icon icon="fa-exclamation-triangle" type="warning" />
                                            <ui-heading tag="h4">Internal Infrastructure Targets</ui-heading>
                                        </ui-layout>
                                        <span>
                                            You have specified internal network targets (private IP ranges: 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16), 
                                            but your organisation does not have an internal scan hub configured.
                                        </span>
                                    </ui-layout>

                                    <ui-panel-base class="panel warning-panel">
                                        <ui-layout direction="column" gap="2">
                                            <ui-heading tag="h5">
                                                <ui-icon icon="fa-info-circle" type="info" />
                                                What is an Internal Scan Hub?
                                            </ui-heading>
                                            <span>
                                                An internal scan hub is a component deployed within your internal network that enables 
                                                scanning of private/internal IP addresses. Without an internal hub, we cannot reach 
                                                internal targets from our external scanning infrastructure.
                                            </span>
                                            <span>
                                                <strong>Internal targets detected:</strong>
                                            </span>
                                            <ul>
                                                <li v-for="target in allValidatedTargets.filter(t => t.is_internal)" :key="target.target">
                                                    {{ target.target }} ({{ target.type }})
                                                </li>
                                            </ul>
                                        </ui-layout>
                                    </ui-panel-base>

                                    <ui-layout direction="column" gap="2">
                                        <ui-heading tag="h5">Next Steps</ui-heading>
                                        <span>
                                            Please contact your account manager or support to arrange deployment of an internal scan hub, 
                                            or remove the internal targets from your scope to continue.
                                        </span>
                                    </ui-layout>
                                </ui-layout>
                            </apck-wizard-pane>

                            <!-- Review and Submit Step -->
                            <apck-wizard-pane title="Review">
                                <ui-layout direction="column" gap="4">
                                    <ui-layout direction="column" gap="2">
                                        <ui-layout gap="2" align="center">
                                            <ui-icon icon="fa-clipboard-check" type="warning" />
                                            <ui-heading tag="h4">Review Your Scope</ui-heading>
                                        </ui-layout>
                                        <span>
                                            Please review the targets you're adding to your scope. Once added, targets 
                                            cannot be removed until your licence anniversary date.
                                        </span>
                                    </ui-layout>

                                    <!-- Validation Errors Warning -->
                                    <ui-panel-base v-if="hasValidationErrors" class="panel error-panel">
                                        <ui-layout gap="2" align="center">
                                            <ui-icon icon="fa-times-circle" type="destructive" />
                                            <span class="error-message">
                                                <strong>Some targets have validation errors and will not be added to scope.</strong>
                                                Please review the summary below and correct any issues before proceeding.
                                            </span>
                                        </ui-layout>
                                    </ui-panel-base>

                                    <!-- Overlap Warning (Server-side - already in scope) -->
                                    <ui-panel-base v-if="targetsWithOverlaps.length > 0" class="panel warning-panel">
                                        <ui-layout direction="column" gap="2">
                                            <ui-layout gap="2" align="center">
                                                <ui-icon icon="fa-triangle-exclamation" type="warning" />
                                                <ui-heading tag="h5">Target Overlap Detected</ui-heading>
                                            </ui-layout>
                                            <span>
                                                The following targets are being added with a different scan type, but already exist in your scope:
                                            </span>
                                            <ul>
                                                <li v-for="target in targetsWithOverlaps" :key="target.target + target.type">
                                                    <strong>{{ target.target }}</strong> ({{ target.type }}) - {{ target.overlap_message }}
                                                </li>
                                            </ul>
                                            <span>
                                                This is allowed and both scan types will be configured for these targets.
                                            </span>
                                        </ui-layout>
                                    </ui-panel-base>

                                    <!-- Client-side Overlap Warning (Multiple types in current session) -->
                                    <ui-panel-base v-if="clientSideOverlaps.length > 0" class="panel warning-panel">
                                        <ui-layout direction="column" gap="2">
                                            <ui-layout gap="2" align="center">
                                                <ui-icon icon="fa-triangle-exclamation" type="warning" />
                                                <ui-heading tag="h5">Multiple Scan Types Detected</ui-heading>
                                            </ui-layout>
                                            <span>
                                                You are adding the following targets with multiple scan types. Please review carefully to ensure this is intended:
                                            </span>
                                            <ul>
                                                <li v-for="overlap in clientSideOverlaps" :key="overlap.fqdn">
                                                    <strong>{{ overlap.fqdn }}</strong> - {{ overlap.types.join(', ') }}
                                                </li>
                                            </ul>
                                            <span>
                                                If this is intentional, both scan types will be configured for these targets. If not, please go back and remove the duplicate entries.
                                            </span>
                                        </ui-layout>
                                    </ui-panel-base>

                                    <!-- Comprehensive Summary Table -->
                                    <ui-layout v-if="allValidatedTargets.length > 0" direction="column" gap="2">
                                        <ui-heading tag="h5">All Targets Summary</ui-heading>
                                        <table class="validation-table summary-table">
                                            <thead>
                                                <tr>
                                                    <th>Target</th>
                                                    <th>Type</th>
                                                    <th>Status</th>
                                                    <th>Network</th>
                                                    <th>Scope Size</th>
                                                    <th>Details</th>
                                                    <th class="action-column">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="target in allValidatedTargets" :key="target.target + target.type" 
                                                    :class="{
                                                        'validation-error': !target.valid || target.is_blacklisted,
                                                        'validation-warning': target.requires_internal_hub || (target.valid && isClientSideOverlap(target)),
                                                        'validation-success': target.valid && !target.is_blacklisted && !target.requires_internal_hub && !isClientSideOverlap(target)
                                                    }">
                                                    <td class="target-cell">
                                                        <ui-icon v-if="isClientSideOverlap(target) && target.valid" icon="fa-copy" type="warning" title="Multiple scan types for this target" />
                                                        {{ target.target }}
                                                    </td>
                                                    <td>
                                                        <ui-badge 
                                                            :label="target.type" 
                                                            :color="target.type === 'Web Application' ? 'blue' : target.type === 'API' ? 'green' : 'red'" 
                                                        />
                                                    </td>
                                                    <td>
                                                        <ui-icon v-if="target.valid && !target.is_blacklisted && !target.requires_internal_hub" icon="fa-check-circle" type="success" />
                                                        <ui-icon v-else-if="target.requires_internal_hub" icon="fa-exclamation-triangle" type="warning" />
                                                        <ui-icon v-else icon="fa-times-circle" type="destructive" />
                                                    </td>
                                                    <td>
                                                        <ui-badge v-if="target.is_internal" label="Internal" color="orange" />
                                                        <ui-badge v-else-if="target.is_external" label="External" color="blue" />
                                                        <ui-badge v-else-if="target.is_application" label="External" color="blue" />
                                                        <ui-badge v-else label="-" color="light-slate" />
                                                    </td>
                                                    <td>{{ target.scope_size || target.size || 1 }}</td>
                                                    <td>
                                                        <span v-if="target.is_blacklisted" class="error-message">Blacklisted</span>
                                                        <span v-else-if="target.error_message" class="error-message">{{ target.error_message }}</span>
                                                        <span v-else-if="target.has_overlap" class="warning-message">
                                                            <ui-icon icon="fa-triangle-exclamation" type="warning" />
                                                            {{ target.overlap_message }}
                                                        </span>
                                                        <span v-else-if="target.requires_internal_hub" class="warning-message">Requires internal hub</span>
                                                        <span v-else-if="target.valid && isClientSideOverlap(target)" class="warning-message">
                                                            <ui-icon icon="fa-copy" type="warning" />
                                                            Multiple scan types for this target
                                                        </span>
                                                        <span v-else-if="target.valid" class="success-message">Ready to add</span>
                                                    </td>
                                                    <td>
                                                        <ui-button
                                                            label="Remove"
                                                            iconLeft="fa-trash"
                                                            color="red"
                                                            size="sm"
                                                            @click="removeTargetFromReview(target)"
                                                            :disabled="submitting"
                                                        />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </ui-layout>

                                    <!-- Summary Statistics -->
                                    <ui-layout v-if="allValidatedTargets.length > 0" gap="4" wrap>
                                        <ui-panel-base class="panel stat-panel">
                                            <ui-layout direction="column" gap="1" align="center">
                                                <ui-heading tag="h3">{{ validatedWebAppTargets.filter(t => t.valid && !t.is_blacklisted).length }}</ui-heading>
                                                <span class="stat-label">Web Applications</span>
                                            </ui-layout>
                                        </ui-panel-base>
                                        <ui-panel-base v-if="hasApiExplorer" class="panel stat-panel">
                                            <ui-layout direction="column" gap="1" align="center">
                                                <ui-heading tag="h3">{{ validatedApiTargets.filter(t => t.valid && !t.is_blacklisted).length }}</ui-heading>
                                                <span class="stat-label">API Targets</span>
                                            </ui-layout>
                                        </ui-panel-base>
                                        <ui-panel-base class="panel stat-panel">
                                            <ui-layout direction="column" gap="1" align="center">
                                                <ui-heading tag="h3">{{ validatedInfraTargets.filter(t => t.valid && !t.is_blacklisted).length }}</ui-heading>
                                                <span class="stat-label">Infrastructure</span>
                                            </ui-layout>
                                        </ui-panel-base>
                                        <ui-panel-base class="panel stat-panel">
                                            <ui-layout direction="column" gap="1" align="center">
                                                <ui-heading tag="h3">{{ allValidatedTargets.reduce((sum, t) => sum + (t.scope_size || t.size || 1), 0) }}</ui-heading>
                                                <span class="stat-label">Total Scope Size</span>
                                            </ui-layout>
                                        </ui-panel-base>
                                    </ui-layout>

                                    <!-- Warning Notice -->
                                    <ui-layout direction="column" gap="2" class="wizard-warning-box">
                                        <ui-layout gap="2" align="center">
                                            <ui-icon icon="fa-triangle-exclamation" type="warning" />
                                            <ui-heading tag="h5" class="wizard-warning-title">Important Notice</ui-heading>
                                        </ui-layout>
                                        <span>
                                            <strong>Permission Required:</strong> You must only include assets and networks that you have explicit permission to scan. 
                                            Unauthorised scanning is illegal and against our terms of service.
                                        </span>
                                        <span>
                                            <strong>Responsibility:</strong> You are fully responsible for ensuring all assets are within your legal rights to scan.
                                        </span>
                                        <span>
                                            <strong>Non-Changeable:</strong> Once added, your scanning scope cannot be modified until your licence anniversary.
                                        </span>
                                    </ui-layout>
                                </ui-layout>
                            </apck-wizard-pane>
                        </apck-wizard>
                    </template>
                </ui-modal>

                <ui-spinner :show="loading || submitting || validating" theme="dark" />
            </template>
        </ui-page>
    `
}
